import React from "react";
import {
  Box,
  Typography,
  FormHelperText,
} from "@mui/material";
import {
  Handshake,
  User,
  Image,
  Link,
  CircleDot,
  Wallet,
  Calendar,
  Upload,
  Trash2,
} from "lucide-react";
import { colors } from "../../../../theme";
import getImageUrlByHash from "../../../../static/logoUtils";

import {
  CommonDialog,
  CommonButton,
  CommonFormField,
  CommonDateTimePicker,
  CommonSwitch,
} from "../../ui";

const PartnerForm = ({
  open,
  onClose,
  onSubmit,
  title,
  subtitle,
  primaryActionLabel,
  isEdit,
  partnerName,
  setPartnerName,
  file,
  setfile,
  LogoHash,
  setLogoHash,
  partnerLogoData,
  partnerURL,
  setPartnerURL,
  status,
  handleSwitchChange,
  PartnerAddress,
  setPartnerAddress,
  CreatedOn,
  setCreatedOn,
  error,
  validated,
}) => {
  const handleRemoveImage = () => {
    if (setfile) setfile(null);
    if (setLogoHash) setLogoHash("");
    const fileInput = document.getElementById("partner-logo-input");
    if (fileInput) fileInput.value = "";
  };
  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      disableScrollLock={false}
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              bgcolor: colors.primary.lighter,
              color: colors.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Handshake size={24} />
          </Box>

          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                color: colors.slate[800],
                fontSize: "1.25rem",
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>

            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  color: colors.slate[500],
                  mt: 0.5,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
      }
      showCloseButton
      maxWidth="md"
      fullWidth
      dividers={false}
      titleSx={{
        px: { xs: 2.5, sm: 4 },
        py: 3,
        borderBottom: `1px solid ${colors.neutral.border}`,
      }}
      contentSx={{
        p: 0,
      }}
      actions={
        <>
          <CommonButton
            variant="cancel"
            onClick={onClose}
          >
            Cancel
          </CommonButton>

          <CommonButton
            variant="primary"
            type="submit"
            form="partner-form"
          >
            {primaryActionLabel}
          </CommonButton>
        </>
      }
      actionsSx={{
        px: { xs: 2.5, sm: 4 },
        py: 2,
        borderTop: `1px solid ${colors.neutral.border}`,
        flexShrink: 0,
      }}
    >
      <form
        id="partner-form"
        onSubmit={onSubmit}
        noValidate
      >
        <Box
          sx={{
            px: { xs: 2.5, sm: 4 },
            py: 3,
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          {/* Partner Name */}
          <CommonFormField
            id="partner-name"
            label="Name"
            icon={<User size={16} />}
            required
            placeholder="Enter partner name"
            value={partnerName}
            onChange={(event) => setPartnerName(event.target.value)}
            error={validated && !partnerName}
            helperText={validated && !partnerName ? "Partner name is required" : undefined}
          />

          {/* Brand Logo */}
          <CommonFormField
            label="Brand Logo"
            icon={<Image size={16} />}
            required={!isEdit}
            error={validated && !file && !isEdit}
            helperText={validated && !file && !isEdit ? "Select Brand Logo" : undefined}
          >
            <Box
              sx={{
                border: `1px dashed ${colors.slate[300]}`,
                borderRadius: "12px",
                p: 3,
                display: "flex",
                alignItems: "center",
                gap: 3,
                position: "relative",
                flexWrap: {
                  xs: "wrap",
                  sm: "nowrap",
                },
              }}
            >
              {/* Logo Preview */}
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "12px",
                  bgcolor: colors.slate[50],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                {file instanceof Blob ? (
                  <Box
                    component="img"
                    src={URL.createObjectURL(file)}
                    alt="Preview"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : isEdit && LogoHash ? (
                  <Box
                    component="img"
                    src={getImageUrlByHash(
                      LogoHash,
                      partnerLogoData
                    )}
                    alt="Current Logo"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Image
                    size={24}
                    color={colors.slate[400]}
                  />
                )}
              </Box>

              {/* Upload Information */}
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: colors.text.primary,
                    wordBreak: "break-word",
                  }}
                >
                  {file ? file.name : (isEdit && LogoHash ? "Current partner logo" : "Choose a logo for the partner")}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "12px",
                    color: colors.slate[500],
                    mt: 0.5,
                  }}
                >
                  Supported formats: PNG, JPG, JPEG (Max 2MB)
                </Typography>
              </Box>

              {/* Actions: Choose File & Remove Image */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
                {(file || (isEdit && LogoHash)) && (
                  <CommonButton
                    variant="dangerText"
                    size="small"
                    startIcon={<Trash2 size={16} />}
                    onClick={handleRemoveImage}
                  >
                    Remove
                  </CommonButton>
                )}

                <CommonButton
                  variant="outlined"
                  size="small"
                  startIcon={<Upload size={16} />}
                  onClick={() =>
                    document
                      .getElementById("partner-logo-input")
                      ?.click()
                  }
                >
                  {file || (isEdit && LogoHash) ? "Change File" : "Choose File"}
                </CommonButton>
              </Box>

              <Box
                id="partner-logo-input"
                component="input"
                type="file"
                sx={{ display: "none" }}
                onChange={(event) => {
                  if (event?.target?.files?.[0]) {
                    setfile(event.target.files[0]);
                  }
                  event.target.value = "";
                }}
                accept="image/*"
              />
            </Box>
          </CommonFormField>

          {/* Website URL */}
          <CommonFormField
            id="partner-url"
            label="Website URL"
            icon={<Link size={16} />}
            required
            placeholder="https://www.example.com"
            value={partnerURL}
            onChange={(event) => setPartnerURL(event.target.value)}
            startAdornment={
              <Link
                size={16}
                color={colors.slate[400]}
              />
            }
            error={validated && !partnerURL}
            helperText={validated && !partnerURL ? "Website URL is required" : undefined}
          />

          {/* Status */}
          <CommonSwitch
            label="Status"
            icon={<CircleDot size={16} />}
            checked={status}
            onChange={handleSwitchChange}
            activeLabel="Active"
            inactiveLabel="Inactive"
          />

          {/* Wallet Address */}
          <CommonFormField
            id="partner-wallet-address"
            label="Wallet Address"
            icon={<Wallet size={16} />}
            required
            placeholder="Enter wallet address (0x...)"
            value={PartnerAddress}
            onChange={(event) => setPartnerAddress(event.target.value)}
            startAdornment={
              <Wallet
                size={16}
                color={colors.slate[400]}
              />
            }
            error={validated && !PartnerAddress}
            helperText={validated && !PartnerAddress ? "Wallet address is required" : undefined}
          />

          {/* Created On */}
          <CommonDateTimePicker
            label="Created On"
            icon={<Calendar size={16} />}
            value={CreatedOn}
            onChange={(value) => setCreatedOn(value)}
          />

          {/* General Error */}
          {error && (
            <FormHelperText
              error
              sx={{
                mt: 1,
                textAlign: "center",
                fontSize: "0.875rem",
              }}
            >
              {error}
            </FormHelperText>
          )}
        </Box>
      </form>
    </CommonDialog>
  );
};


export default PartnerForm;